import React, {
  useMemo,
  useState,
  useEffect,
} from "react";
import axios from "axios";
import api from "../api";
import { apiFetch } from "../apiFetch";

export default function RootCauseAnalysisModal({
  configId,
  open,
  loading,
  rowDetails,
  transactionId,
  onClose,
  onRulesChange
}) {
  const [expandedRow, setExpandedRow] = useState(null);

  const [rules, setRules] = useState([]);

  const [selectedRules, setSelectedRules] = useState({});

  const [showMatched, setShowMatched] = useState(false);

  const [saveForFuture, setSaveForFuture] = useState(false);
  /* =====================================
     Load Transformation Rules
  ===================================== */

  useEffect(() => {
    if (!open) return;

    loadRules();
  }, [open]);

   useEffect(() => {
   
    const transformationRules = Object.values(selectedRules);
    onRulesChange(transformationRules)
  }, [selectedRules]);

  const loadRules = async () => {
    try {
      const res = await api.get("transformation-rules");

      setRules(res.data || []);
    } catch (err) {
      console.error(
        "Failed to load rules",
        err
      );
    }
  };

  /* =====================================
     Build Grouped Data
  ===================================== */

  const groupedData = useMemo(() => {
    if (!rowDetails) return {};

    const rows = rowDetails?.data || [];
    const relations =
      rowDetails?.relation || [];

    const grouped = {
      "INPUT COLUMN": [],
      "CUSTOM COLUMN": [],
      "OUTPUT COLUMN": [],
      "OTHER COLUMN" : [],
      "FINAL OUTPUT COLUMN": [],
    };

    const dependencyColumns =
      new Set();

    relations.forEach((r) => {
      r.dependencies?.forEach((d) => {
        dependencyColumns.add(
          d.dependencyColumnName
            ?.trim()
            .toLowerCase()
        );
      });
    });

    rows.forEach((item) => {
      const field =
        item.s_source_column_name;

      const relation = relations.find(
        (r) =>
          r.columnName
            ?.trim()
            .toLowerCase() ===
          field?.trim().toLowerCase()
      );

      if (!grouped[item.column_type])
        return;

      grouped[item.column_type].push({
        field,
        source:
          item.s_column_value ?? "-",
        target:
          item.t_column_value ?? "-",
        match: item.isMatched,
        sourceIndex:
          item.s_column_index,
        targetIndex:
          item.s_target_index,

        dependsOn:
          relation?.dependencies
            ?.sort(
              (a, b) =>
                a.executionOrder -
                b.executionOrder
            )
            ?.map(
              (d) =>
                d.dependencyColumnName
            )
            ?.join(", ") || "-",

        impact:
          relation?.propagationImpact ||
          "-",

        isDependOn:
          dependencyColumns.has(
            field
              ?.trim()
              .toLowerCase()
          ),
      });
    });

    return grouped;
  }, [rowDetails]);

  /* =====================================
     Summary
  ===================================== */

  const allRows = useMemo(
    () =>
      Object.values(
        groupedData || {}
      ).flat(),
    [groupedData]
  );

  const rootCauses = useMemo(
    () =>
      allRows.filter(
        (r) =>
          !r.match &&
          r.dependsOn === "-"
      ),
    [allRows]
  );

  const impactedRows = useMemo(
    () =>
      allRows.filter(
        (r) =>
          !r.match &&
          r.dependsOn !== "-"
      ),
    [allRows]
  );

  const totalCount = allRows.length;

  const matchedCount =  allRows.filter((r) => r.match).length;

  const mismatchCount = allRows.filter((r) => !r.match).length;

  function handleRecalculate(){
    const transformationRules = Object.values(selectedRules);
    apiFetch("file-mapping-config/update-transform-rules/"+configId,{
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(transformationRules)})
      .then((res) => res.json())
      .then((res) => alert(JSON.stringify(res.status)))
      .catch("setError")
      .finally(() => "")
    
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 p-6 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] h-[92vh] overflow-hidden flex flex-col">
        {/* HEADER */}

        <div className="border-b bg-white">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">
                Root Cause Analysis 
              </h2>

              <div className="text-mb  text-gray-800 mt-1">
                Transaction:{" "}
                {transactionId} <> &nbsp;&nbsp;     </> 
                <button
                  onClick={() =>
                    setShowMatched(!showMatched)
                  }
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    showMatched
                      ? "bg-green-100 text-green-700"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {showMatched
                    ? " Showing All Columns"
                    : " Showing Mismatches Only"}
              </button>
                
              </div>
              

            </div>
            
            <button
              onClick={onClose}
              className="text-2xl text-gray-500 hover:text-black"
            >
              ✕
            </button>
          </div>

          {/* Summary */}

          <div className="px-6 py-3 bg-slate-50 border-t text-sm flex flex-wrap gap-6">
              <span className="font-bold ml-1">
                 Columns summary 
              </span>

              <span>
                Total:
                <span className="font-bold ml-1">
                  {totalCount}
                </span>
              </span>

              <span className="text-green-600">
                Matched:
                <span className="font-bold ml-1">
                  {matchedCount}
                </span>
              </span>

              <span className="text-red-600">
                Mismatch:
                <span className="font-bold ml-1">
                  {mismatchCount}
                </span>
              </span>

              <span className="text-red-600">
                Root Cause:
                <span className="font-bold ml-1">
                  {rootCauses.length}
                </span>
              </span>

              <span className="text-amber-600">
                Impacted:
                <span className="font-bold ml-1">
                  {impactedRows.length}
                </span>
              </span>

            </div>
        </div>

        {/* BODY */}

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            Loading...
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <table className="w-full min-w-[1400px] text-sm">
              <thead className="sticky top-0 bg-white z-20">
                <tr className="border-b">
                  <th className="px-4 py-3 text-left">
                    Field
                  </th>

                  <th className="px-4 py-3 text-left">
                    Source
                  </th>

                  <th className="px-4 py-3 text-left">
                    Target
                  </th>

                  <th className="w-[120px] px-4 py-3 text-center">
                    Status
                  </th>

                  <th className="w-[400px] px-4 py-3 text-left">
                    Dependencies
                  </th>

                  <th className="px-4 py-3 text-left">
                    Transform Rule
                  </th>

                  {/* <th className="px-4 py-3 text-center">
                    Action
                  </th> */}
                </tr>
              </thead>

              <tbody>
                {Object.entries(
                  groupedData
                ).map(
                  ([groupName, rows]) =>
                    rows.filter(
                      (r) =>
                        showMatched || !r.match
                    ).length > 0 && (
                      <React.Fragment
                        key={groupName}
                      >
                        <tr className="bg-slate-100">
                          <td
                            colSpan={7}
                            className="px-4 py-3 font-bold"
                          >
                            {groupName}
                            <span className="ml-2 text-xs text-gray-500">
                              (
                                  {
                                    rows.filter(
                                      (r) =>
                                        showMatched || !r.match
                                    ).length
                                  }
                              )
                            </span>
                          </td>
                        </tr>

                        {rows
                          .filter(
                            (row) =>
                              showMatched || !row.match
                          ).map(
                          (row) => {
                            const isRoot =
                              !row.match &&
                              row.dependsOn ===
                                "-";

                            const isImpacted =
                              !row.match &&
                              row.dependsOn !==
                                "-";

                            return (<React.Fragment key={row.field}>
                                  <tr
                                    className={
                                      isRoot
                                        ? "bg-red-50"
                                        : isImpacted
                                        ? "bg-yellow-50"
                                        : ""
                                    }
                                  >
                                <td className="px-4 py-4 border-b font-medium">
                                  {
                                    row.field
                                  }
                                </td>

                                <td className="px-4 py-4 border-b">
                                  {
                                    row.source
                                  }
                                </td>

                                <td className="px-4 py-4 border-b">
                                  {
                                    row.target
                                  }
                                </td>

                               <td className="w-[120px] px-4 py-4 border-b text-center">
                                  {row.match ? (
                                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs">
                                      Match
                                    </span>
                                  ) : isRoot ? (
                                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs">
                                      Root
                                      Cause
                                    </span>
                                  ) : (
                                    <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs">
                                      Impacted
                                    </span>
                                  )}
                                </td>

                                <td className="w-[400px] px-4 py-4 border-b">
                                  {row.dependsOn !==
                                  "-" ? (
                                    <div className="flex flex-wrap gap-2">
                                      {row.dependsOn
                                        .split(
                                          ","
                                        )
                                        .map(
                                          (
                                            dep
                                          ) => (
                                            <span
                                              key={
                                                dep
                                              }
                                              className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs"
                                            >
                                              {dep.trim()}
                                            </span>
                                          )
                                        )}
                                    </div>
                                  ) : (
                                    "-"
                                  )}
                                </td>

                                <td className="px-4 py-4 border-b">
                                  {!row.match ? (
                                    <select
                                      className="border rounded px-2 py-1 text-xs w-full"
                                      value={selectedRules[row.field]?.ruleId || ""}
                                      onChange={(e) =>
                                        setSelectedRules((prev) => ({
                                          ...prev,
                                          [row.field]: {
                                            field: row.field,
                                            sourceIndex: row.sourceIndex,
                                            targetIndex: row.targetIndex,
                                            ruleId: e.target.value,
                                          },
                                        }))
                                      }
                                    >
                                      <option value="">Select Rule</option>

                                      {rules.map((rule) => (
                                        <option
                                          key={rule.id}
                                          value={rule.id}
                                        >
                                          {rule.ruleName}
                                        </option>
                                      ))}
                                    </select>
                                   
                                  ) : (
                                    "-"
                                  )}
                                </td>

                                
                              </tr>
                                </React.Fragment>

                            );
                          }
                        )}
                      </React.Fragment>
                    )
                )}
              </tbody>
            </table>
          </div>
        )}


           <div className="border-t bg-white px-6 py-4 flex items-center justify-between">

  <div className="flex items-center gap-4">

    <label className="flex items-center gap-2 text-sm cursor-pointer">
      {/* <input
        type="checkbox"
        checked={saveForFuture}
        onChange={(e) =>
          setSaveForFuture(e.target.checked)
        }
      /> */}

      Save selected transformation rules for future runs
    </label>

    <span className="text-gray-500 text-sm">
      {Object.keys(selectedRules).length}
      {" "}rule(s) selected
    </span>

  </div>

  <div className="flex gap-3">

    <button
      onClick={onClose}
      className="px-4 py-2 border rounded-lg"
    >
      Cancel
    </button>

    <button
      onClick={handleRecalculate}
      className="px-5 py-2 bg-blue-600 text-white rounded-lg"
      disabled={Object.values(selectedRules).length>0?false:true}
    >
      Save Config
    </button>

  </div>

</div>
      
      </div>

    </div>
    
  );
}
